import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import {
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { Button } from './Button';
import { cx } from './cx';

/**
 * Asks before something that can't be undone, or collects a few fields in place. A native
 * <dialog>, so focus stays inside and Escape closes it.
 */
export function Dialog({
  open,
  title,
  footer,
  onClose,
  children,
}: {
  open: boolean;
  title: ReactNode;
  /** The actions: Cancel first, the main one last. */
  footer?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close?.();
  }, [open]);
  if (!open) return null;
  return (
    <dialog
      ref={ref}
      className="mz-dialog mz-dialog-native"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="mz-dialog-head">
        <h2 className="mz-dialog-title" id={titleId}>
          {title}
        </h2>
        <Button variant="ghost" size="sm" icon={X} label="Close" onClick={onClose} />
      </div>
      <div className="mz-dialog-body">{children}</div>
      {footer != null && <div className="mz-dialog-foot">{footer}</div>}
    </dialog>
  );
}

export interface MenuItem {
  label: string;
  icon?: LucideIcon;
  /** Text at the right, such as the current choice. */
  hint?: string;
  onSelect?: () => void;
  /** A link instead of an action. */
  href?: string;
  danger?: boolean;
}

/**
 * A button that opens a list of actions. `trigger` gets the props that open the menu;
 * arrows move between items, Escape closes it.
 */
export function Menu({
  label,
  trigger,
  header,
  items,
  align = 'start',
  placement = 'below',
}: {
  label: string;
  trigger: (props: {
    onClick: () => void;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
  }) => ReactElement;
  header?: ReactNode;
  items: (MenuItem | 'separator')[];
  align?: 'start' | 'end';
  placement?: 'below' | 'above';
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    box.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    function outside(event: MouseEvent) {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const all = [...(box.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const at = all.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'Escape') {
      setOpen(false);
      box.current?.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = (at + (event.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length;
      all[next]?.focus();
    }
  }

  const position =
    placement === 'above' ? { bottom: 'calc(100% + 4px)' } : { top: 'calc(100% + 4px)' };
  return (
    <div ref={box} style={{ position: 'relative' }}>
      {trigger({
        onClick: () => setOpen((value) => !value),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': id,
      })}
      {open && (
        <div
          id={id}
          className="mz-menu"
          role="menu"
          aria-label={label}
          onKeyDown={onKeyDown}
          style={{
            position: 'absolute',
            zIndex: 50,
            ...position,
            ...(align === 'end' ? { right: 0 } : { left: 0 }),
          }}
        >
          {header != null && <div className="mz-menu-head">{header}</div>}
          {items.map((item, i) => {
            if (item === 'separator') {
              // biome-ignore lint/suspicious/noArrayIndexKey: separators have no identity
              return <hr key={i} className="mz-menu-sep" />;
            }
            const Icon = item.icon;
            const inner = (
              <>
                {Icon && (
                  <Icon size={16} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
                )}
                <span>{item.label}</span>
                {item.hint && <span className="mz-menu-hint">{item.hint}</span>}
              </>
            );
            const className = cx('mz-menu-item', item.danger && 'mz-menu-item-danger');
            return item.href ? (
              <a
                key={item.label}
                href={item.href}
                role="menuitem"
                className={className}
                onClick={() => setOpen(false)}
              >
                {inner}
              </a>
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                className={className}
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
